/**
 * The application's one status vocabulary.
 *
 * Three different things in this product have a state — an ingestion job, a
 * judgment, and a capture's completeness — and each used to style itself where
 * it happened to be rendered. That is how a failed capture ended up wearing a
 * green "Complete" badge on one screen while the database said otherwise.
 *
 * Every state is now declared once, here, as data. A screen chooses a
 * presentation (`badge`, `dot`, `inline`); it never chooses a colour.
 */
import type { JobStatus, JudgmentStatus } from '@/types/api'

export type StatusTone =
  | 'neutral'
  | 'accent'
  | 'good'
  | 'warning'
  | 'serious'
  | 'critical'

export interface StatusMeta {
  /** Always rendered. Colour is reinforcement, never the only signal. */
  label: string
  tone: StatusTone
  /** Detail for a tooltip. Never needed to understand the label. */
  hint?: string
  /** The state is in motion: draw a spinner in place of the dot. */
  busy?: boolean
}

/** Ingestion jobs. */
export const JOB_STATUS_META: Record<JobStatus, StatusMeta> = {
  pending: { label: 'Queued', tone: 'neutral', hint: 'Waiting for a worker.' },
  running: { label: 'Running', tone: 'accent', busy: true, hint: 'In progress.' },
  succeeded: {
    label: 'Complete',
    tone: 'good',
    hint: 'Everything the portal displayed was collected.',
  },
  // "Partial" now means one thing only: the portal displayed a page this run
  // did not read, so records that were on offer are missing. It used to also
  // cover a search that hit the portal's 300-record display cap, which is a
  // property of the source rather than a shortfall in the run — a capped
  // search that read all fifteen of its pages has everything available, and
  // badging that "Partial" told the operator to re-run finished work.
  partial: {
    label: 'Partial',
    tone: 'warning',
    hint: 'A page the portal displayed was not read. Re-run to collect it.',
  },
  failed: { label: 'Failed', tone: 'critical', hint: 'Stopped before finishing.' },
}

/** A judgment's own status, as the docket reports it. */
export const JUDGMENT_STATUS_META: Record<JudgmentStatus, StatusMeta> = {
  open: { label: 'Open', tone: 'warning' },
  closed: { label: 'Closed', tone: 'good' },
  amended: { label: 'Amended', tone: 'accent' },
  revived: { label: 'Revived', tone: 'serious' },
  voided: { label: 'Voided', tone: 'critical' },
  unknown: { label: 'Unknown', tone: 'neutral' },
}

/** Kept for the few places that want the word without the presentation. */
export const STATUS_LABELS: Record<JudgmentStatus, string> = {
  open: 'Open',
  closed: 'Closed',
  amended: 'Amended',
  revived: 'Revived',
  voided: 'Voided',
  unknown: 'Unknown',
}

/** The shape a capture verdict needs; narrower than the full `Capture`. */
export interface CaptureOutcome {
  isCapped: boolean
  isComplete: boolean
  /** The portal ran the search and holds no matching records. */
  noRecords?: boolean
  /** How many records the run actually stored. Separates partial from failed. */
  recordsFound?: number
  verdict?: string
}

/**
 * A capture's completeness.
 *
 * Five states. The ordering is the substance of this function: completeness is
 * decided *before* the display cap is mentioned, because they answer different
 * questions and only the first one is about whether this run did its job.
 *
 *   Partial          the portal displayed a page this run did not read, and it
 *                    stored something. Records that were on offer are missing;
 *                    re-running tops them up, because ingest de-duplicates.
 *   Failed           the run stored nothing at all. There is no data here.
 *   None on source   the portal ran the search and holds no match. A finished
 *                    search with an empty answer — nothing to fix, nothing to
 *                    re-run. This used to render as a red failure, which sent
 *                    people to the browser to debug a crawler that was fine.
 *   Complete         everything the portal displayed was stored.
 *   Complete, capped as above, and the portal also refused to display matches
 *                    beyond its 300-record ceiling. Still a success: the run
 *                    took everything on offer. Splitting the search reaches
 *                    what the portal will not show in one go.
 *
 * `isCapped` no longer short-circuits to a warning. A capped search that read
 * all of its pages is complete, and badging it "Capped at 300" in amber made
 * finished work look like a problem to chase.
 */
export function captureStatusMeta(capture: CaptureOutcome): StatusMeta {
  if (!capture.isComplete) {
    // recordsFound is what separates the two. Treated as 0 when absent, so a
    // caller that cannot supply it degrades to the more cautious verdict
    // rather than silently claiming data exists.
    const stored = capture.recordsFound ?? 0
    return stored > 0
      ? {
          label: 'Partial',
          tone: 'serious',
          hint:
            capture.verdict ||
            'The run stopped early. What it read was stored and is usable.',
        }
      : {
          label: 'Failed',
          tone: 'critical',
          hint: capture.verdict || 'The run stored nothing.',
        }
  }
  if (capture.noRecords) {
    return {
      label: 'None on source',
      tone: 'neutral',
      hint:
        capture.verdict ||
        'The portal ran this search and holds no matching judgments. Nothing was missed.',
    }
  }
  if (capture.isCapped) {
    return {
      label: 'Complete, capped',
      tone: 'good',
      hint:
        capture.verdict ||
        'Every page the portal displayed was read. It caps display at 300, so further matches exist that it will not show — split the search to reach them.',
    }
  }
  return {
    label: 'Complete',
    tone: 'good',
    hint: capture.verdict || 'Every match the portal reported was stored.',
  }
}
